package com.wintermar.inspection.core.ui.theme

import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Dimension — design system layout tokens
 *
 * Centralized layout tokens used across Compose UI. Mirrors Figma design tokens so code and designs
 * share the same scale. Prefer these semantic tokens instead of ad-hoc `dp` values.
 *
 * Structure
 * - spacing: atomic spacing tokens (space0..space13) for margins/gaps/padding.
 * - radius: corner radii (none, s, m, l, pill).
 * - size: semantic sizes for components and icons (icon.*, xxs..xxl, size1..size15).
 *
 * Figma mapping (examples)
 * - Spacing:
 *   - `space0 = 0.dp`  // Figma: spacing/space-0 (0px)
 *   - `space1 = 4.dp`  // Figma: spacing/space-1 (4px)
 *   - `space2 = 8.dp`  // Figma: spacing/space-2 (8px)
 *
 * - Radius:
 *   - `none = 0.dp`    // Figma: radius/none
 *   - `s = 4.dp`       // Figma: radius/s
 *   - `m = 8.dp`       // Figma: radius/m
 *   - `l = 16.dp`      // Figma: radius/l
 *   - `pill = 999.dp`  // Figma: radius/pill (full pill)
 *
 * - Sizes:
 *   - Icon sizes: `icon.xxs = 8.dp`, `icon.xs = 16.dp`, `icon.s = 24.dp`, `icon.m = 40.dp`, ...
 *   - Semantic sizes: `xxs = 4.dp`, `xs = 8.dp`, `s = 16.dp`, `m = 24.dp`, `l = 32.dp`, `xl = 40.dp`
 *   - Fine-grained: `size1..size15` provide component-specific sizing scale.
 *
 * Guidelines
 * - Use semantic tokens (e.g. `dims.size.m`) so components follow global changes.
 * - Use `spacing` for paddings/gaps, `size` for component dimensions and icons, `radius` for shapes.
 * - Component APIs may accept `Dp` but should default to these tokens.
 *
 * Compose usage
 * - Obtain tokens:
 *   - `val dims = AppDimension() // or obtain from Theme provider`
 * - Padding:
 *   - `Modifier.padding(dims.spacing.space4)` // 16.dp
 * - Icon:
 *   - `Modifier.size(dims.size.icon.s)` // 24.dp
 * - Shape:
 *   - `RoundedCornerShape(dims.radius.m)` // 8.dp
 *
 * Syncing with Figma
 * - Keep Figma token names/values aligned. When tokens change in Figma, update these defaults
 *   and record a short changelog entry.
 */
data class AppDimension(
    val spacing: SpaceDimension = SpaceDimension(),
    val radius: RadiusDimension = RadiusDimension(),
    val size: SemanticSizeDimension = SemanticSizeDimension()
)

data class SpaceDimension(
    val space0: Dp = 0.dp,
    val space1: Dp = 4.dp,
    val space2: Dp = 8.dp,
    val space3: Dp = 12.dp,
    val space4: Dp = 16.dp,
    val space5: Dp = 24.dp,
    val space6: Dp = 32.dp,
    val space7: Dp = 40.dp,
    val space8: Dp = 48.dp,
    val space9: Dp = 64.dp,
    val space10: Dp = 80.dp,
    val space11: Dp = 96.dp,
    val space12: Dp = 112.dp,
    val space13: Dp = 128.dp,
)

data class SemanticSizeDimension(
    val icon: IconDimension = IconDimension(),
    val xxs: Dp = 4.dp,
    val xs: Dp = 8.dp,
    val s: Dp = 16.dp,
    val m: Dp = 24.dp,
    val l: Dp = 32.dp,
    val xl: Dp = 40.dp,
    val xxl: Dp = 48.dp,

    val size0: Dp = 0.dp,
    val size1: Dp = 4.dp,
    val size2: Dp = 8.dp,
    val size3: Dp = 12.dp,
    val size4: Dp = 16.dp,
    val size5: Dp = 20.dp,
    val size6: Dp = 24.dp,
    val size7: Dp = 32.dp,
    val size8: Dp = 40.dp,
    val size9: Dp = 48.dp,
    val size10: Dp = 56.dp,
    val size11: Dp = 64.dp,
    val size12: Dp = 80.dp,
    val size13: Dp = 96.dp,
    val size14: Dp = 112.dp,
    val size15: Dp = 128.dp
)

data class RadiusDimension(
    val none: Dp = 0.dp,
    val sm: Dp = 4.dp,
    val md: Dp = 8.dp,
    val lg: Dp = 16.dp,
    val xl: Dp = 32.dp,
    val `2xl`: Dp = 128.dp,
    val `3xl`: Dp = 360.dp,
    val full: Dp = 9999.dp
)

data class IconDimension(
    val xxs: Dp = 8.dp,
    val xs: Dp = 16.dp,
    val s: Dp = 24.dp,
    val m: Dp = 40.dp,
    val l: Dp = 48.dp,
    val xl: Dp = 64.dp,
    val xxl: Dp = 80.dp
)
